import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2 } from "lucide-react";
import { LEGAL_DOCS, LEGAL_ORDER } from "@/lib/legalContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return null;
  }
};

const LegalPage = ({ doc }) => {
  const [info, setInfo] = useState(null);
  const config = LEGAL_DOCS[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/legal`).then((res) => setInfo(res.data)).catch(() => setInfo({}));
  }, [doc]);

  if (!info) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const sections = config.sections(info);
  const updated = prettyDate(info.updated_at);

  return (
    <div className="min-h-screen bg-[#020617]" data-testid={`legal-page-${doc}`}>
      <header className="border-b border-slate-800 px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-[#D4AF37] text-sm transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Bad Boss <span className="text-[#D4AF37]">Clinic</span>
          </Link>
          <p className="text-slate-600 text-[10px] uppercase tracking-[0.22em]">{info.entity_line}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-[#38BDF8] text-xs uppercase tracking-[0.3em] mb-4">{config.kicker}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-white mb-6" data-testid="legal-title">
          {config.title}
        </h1>
        <p className="text-slate-400 text-base leading-relaxed mb-3 max-w-2xl">{config.intro(info)}</p>
        {updated && <p className="text-slate-600 text-xs uppercase tracking-[0.2em]">Last updated {updated}</p>}

        <div className="mt-14 space-y-12">
          {sections.map((section, idx) => (
            <section key={section.heading} data-testid={`legal-section-${idx}`}>
              <h2 className="text-white text-lg font-medium mb-4 pb-3 border-b border-slate-800">
                {section.heading}
              </h2>
              <ul className="space-y-3">
                {section.body.map((line, i) => (
                  <li key={i} className="text-slate-400 text-sm leading-relaxed flex gap-3">
                    <span className="text-[#D4AF37]/50 select-none">—</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <nav className="mt-20 pt-8 border-t border-slate-800 flex flex-wrap gap-x-8 gap-y-3">
          {LEGAL_ORDER.filter((key) => key !== doc).map((key) => (
            <Link
              key={key}
              to={`/${key}`}
              className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors"
              data-testid={`legal-link-${key}`}
            >
              {LEGAL_DOCS[key].title} →
            </Link>
          ))}
        </nav>

        <p className="text-slate-600 text-xs leading-relaxed mt-10">
          {info.entity}
          {info.address ? ` · ${info.address}` : ""}
          {info.registration ? ` · ${info.registration}` : ""}
          {info.vat_number ? ` · VAT ${info.vat_number}` : ""}
          {info.contact_email ? ` · ${info.contact_email}` : ""}
        </p>
      </main>
    </div>
  );
};

export default LegalPage;
